import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Search, X, Filter, Printer } from "lucide-react";
import ExportButton from "@/components/ExportButton";
import { ValorizacionInventario } from "@/components/ValorizacionInventario";
import { CierresInventario } from "@/components/CierresInventario";

// Genera HTML print-ready y abre el diálogo de impresión del navegador
function imprimirHojaConteo({ rows, columns, totales, filters }) {
  const win = window.open("", "_blank", "width=1100,height=800");
  if (!win) return;
  const today = new Date().toLocaleDateString("es-EC", { year: "numeric", month: "long", day: "numeric" });
  const filtTxt = [
    filters.bodega_id !== "all" ? `Bodega: ${filters._bodegaLabel}` : null,
    filters.laboratorio_id !== "all" ? `Lab: ${filters._labLabel}` : null,
    filters.lotes_por_vencer_dias ? `Vencen en ≤ ${filters.lotes_por_vencer_dias} días` : null,
    filters.solo_vencidos ? "Solo vencidos" : null,
    filters.solo_con_stock ? "Solo con stock" : null,
  ].filter(Boolean).join(" · ") || "Todos";
  const cellFmt = (v, key) => {
    if (v == null || v === "") return "";
    if (["costo_unitario", "costo_caja", "valor_inventario", "valor_inventario_caja", "precio_venta"].includes(key)) return "$" + (+v).toFixed(2);
    if (["cantidad_disponible", "unidades_por_caja", "cantidad_cajas", "cantidad_sueltas"].includes(key)) return (+v).toLocaleString("es-EC");
    return String(v);
  };
  const head = columns.map(c => `<th>${c.label}</th>`).join("") + `<th class="conteo">Conteo físico</th><th class="dif">Diferencia</th>`;
  const body = rows.map(r => `<tr>${columns.map(c => `<td>${cellFmt(r[c.key], c.key)}</td>`).join("")}<td class="conteo"></td><td class="dif"></td></tr>`).join("");
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Hoja de conteo de inventario</title>
  <style>
    *{box-sizing:border-box;}
    body{font-family:Arial,Helvetica,sans-serif;margin:0;padding:14mm;color:#15241d;font-size:10pt;}
    h1{font-size:14pt;margin:0 0 4px;}
    .meta{display:flex;gap:24px;font-size:9pt;color:#4a5c53;margin-bottom:6px;flex-wrap:wrap;}
    .totales{background:#e9f2ee;border:1px solid #b2d0c2;padding:6px 10px;border-radius:4px;margin-bottom:8px;font-size:9pt;display:flex;gap:18px;}
    .firma{margin-top:18px;display:flex;justify-content:space-around;font-size:9pt;color:#4a5c53;}
    .firma div{border-top:1px solid #15241d;padding-top:4px;min-width:170px;text-align:center;}
    table{width:100%;border-collapse:collapse;font-size:8.5pt;}
    th{background:#15241d;color:#fff;padding:5px 6px;text-align:left;font-weight:600;}
    td{border:1px solid #d1dbd6;padding:4px 6px;}
    tr:nth-child(even) td{background:#f7f9f8;}
    .conteo,.dif{width:60px;text-align:center;background:#fff8e0 !important;}
    .dif{background:#fdecea !important;}
    @media print{
      body{padding:8mm;}
      thead{display:table-header-group;}
      tr{page-break-inside:avoid;}
      .no-print{display:none !important;}
    }
    .no-print{position:fixed;top:8px;right:8px;background:#205c45;color:#fff;border:0;padding:8px 14px;border-radius:4px;cursor:pointer;font-size:11pt;}
  </style></head><body>
  <button class="no-print" onclick="window.print()">🖨️ Imprimir</button>
  <h1>Hoja de conteo físico de inventario</h1>
  <div class="meta"><span><strong>Fecha:</strong> ${today}</span><span><strong>Filtros:</strong> ${filtTxt}</span></div>
  <div class="totales"><span><strong>${totales.n_lotes || 0}</strong> lotes</span><span><strong>${(totales.stock_total || 0).toLocaleString("es-EC")}</strong> uds stock sistema</span><span><strong>$${(totales.valor_total || 0).toFixed(2)}</strong> valor</span></div>
  <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
  <div class="firma">
    <div>Bodeguero / Contador físico<br/>Fecha y firma</div>
    <div>Revisado por<br/>Fecha y firma</div>
  </div>
  </body></html>`);
  win.document.close();
  // Imprimir automáticamente tras carga
  setTimeout(() => { try { win.focus(); win.print(); } catch (_) {} }, 400);
}

// Columnas visibles por defecto (clave)
const DEFAULT_COLS = [
  "codigo_interno", "nombre_comercial", "bodega_nombre", "numero_lote",
  "fecha_vencimiento", "dias_para_vencer",
  "cantidad_disponible", "unidades_por_caja", "cantidad_cajas", "cantidad_sueltas",
  "costo_unitario", "costo_caja", "valor_inventario", "valor_inventario_caja",
];

const numericKeys = new Set([
  "cantidad_disponible", "cantidad_fisica", "cantidad_reservada_pedidos", "unidades_por_caja", "cantidad_cajas", "cantidad_sueltas",
  "costo_unitario", "costo_caja", "valor_inventario", "valor_inventario_caja",
  "precio_venta", "iva_porcentaje", "dias_para_vencer",
]);

export default function InventoryReport() {
  const [columns, setColumns] = useState([]);
  const [selected, setSelected] = useState(new Set(DEFAULT_COLS));
  const [rows, setRows] = useState([]);
  const [totales, setTotales] = useState({ n_lotes: 0, stock_total: 0, valor_total: 0 });
  const [bodegas, setBodegas] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    bodega_id: "all",
    laboratorio_id: "all",
    solo_con_stock: true,
    solo_activos: true,
    solo_vencidos: false,
    descontar_pedidos: false,
    lotes_por_vencer_dias: "",
    buscar: "",
  });

  useEffect(() => {
    Promise.all([
      api.get("/inventory-report/columns"),
      api.get("/masters/catalog/bodegas"),
      api.get("/masters/catalog/laboratorios"),
    ]).then(([c, b, l]) => {
      setColumns(c.data);
      setBodegas(b.data);
      setLabs(l.data);
    }).catch(e => toast.error(formatApiError(e)));
    consultar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const consultar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.bodega_id !== "all") params.bodega_id = filters.bodega_id;
      if (filters.laboratorio_id !== "all") params.laboratorio_id = filters.laboratorio_id;
      params.solo_con_stock = filters.solo_con_stock;
      params.solo_activos = filters.solo_activos;
      params.solo_vencidos = filters.solo_vencidos;
      params.descontar_pedidos = filters.descontar_pedidos;
      if (filters.lotes_por_vencer_dias) params.lotes_por_vencer_dias = parseInt(filters.lotes_por_vencer_dias);
      if (filters.buscar) params.buscar = filters.buscar;
      const { data } = await api.get("/inventory-report/data", { params });
      setRows(data.rows || []);
      setTotales(data.totales || { n_lotes: 0, stock_total: 0, valor_total: 0 });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const toggleCol = (key) => {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const visibleColumns = useMemo(
    () => columns.filter(c => selected.has(c.key)),
    [columns, selected]
  );

  const exportColumns = useMemo(
    () => visibleColumns.map(c => ({ label: c.label, accessor: c.key })),
    [visibleColumns]
  );

  const exportRows = useMemo(
    () => rows.map(r => {
      const o = {};
      for (const c of visibleColumns) {
        let v = r[c.key];
        if (v === true) v = "Sí";
        if (v === false) v = "No";
        o[c.key] = v == null ? "" : v;
      }
      return o;
    }),
    [rows, visibleColumns]
  );

  return (
    <div className="space-y-6" data-testid="inventory-report-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Bodega · Reportes</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Reporte de inventario personalizable</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Elige columnas, filtra por bodega, laboratorio o caducidad y exporta el resultado a Excel o PDF.</p>
        {filters.descontar_pedidos ? (
          <p className="text-xs text-[#8a5a00] mt-1" data-testid="aviso-descontar-pedidos">
            La columna <strong>Stock (uds)</strong> muestra lo realmente libre: se descontó lo comprometido en notas de pedido pendientes.
          </p>
        ) : null}
      </div>

      {/* Filtros */}
      <div className="rounded-md border border-[#d1dbd6] bg-white p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#5c7368]"><Filter size={14} />Filtros</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[11px]">Bodega</Label>
            <Select value={filters.bodega_id} onValueChange={(v) => setFilters(f => ({ ...f, bodega_id: v }))}>
              <SelectTrigger data-testid="filter-bodega"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las bodegas</SelectItem>
                {bodegas.map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Laboratorio</Label>
            <Select value={filters.laboratorio_id} onValueChange={(v) => setFilters(f => ({ ...f, laboratorio_id: v }))}>
              <SelectTrigger data-testid="filter-lab"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los laboratorios</SelectItem>
                {labs.map(l => <SelectItem key={l.id} value={l.id}>{l.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Vencen en (días)</Label>
            <Input type="number" placeholder="Ej: 180" value={filters.lotes_por_vencer_dias} onChange={(e) => setFilters(f => ({ ...f, lotes_por_vencer_dias: e.target.value }))} data-testid="filter-dias" />
          </div>
          <div>
            <Label className="text-[11px]">Buscar</Label>
            <div className="relative">
              <Input className="pl-8" placeholder="Código, nombre, lote..." value={filters.buscar} onChange={(e) => setFilters(f => ({ ...f, buscar: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && consultar()} data-testid="filter-buscar" />
              <Search size={14} className="absolute left-2 top-2.5 text-[#8ba397]" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={filters.solo_con_stock} onCheckedChange={(v) => setFilters(f => ({ ...f, solo_con_stock: !!v }))} data-testid="filter-stock" />
            Solo con stock
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={filters.solo_activos} onCheckedChange={(v) => setFilters(f => ({ ...f, solo_activos: !!v }))} data-testid="filter-activos" />
            Solo lotes activos
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-[#7a2514]">
            <Checkbox checked={filters.solo_vencidos} onCheckedChange={(v) => setFilters(f => ({ ...f, solo_vencidos: !!v }))} data-testid="filter-vencidos" />
            Solo lotes vencidos
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-[#1c4b39] font-medium">
            <Checkbox
              checked={filters.descontar_pedidos}
              onCheckedChange={(v) => {
                setFilters(f => ({ ...f, descontar_pedidos: !!v }));
                setSelected(s => {
                  const n = new Set(s);
                  if (v) { n.add("cantidad_fisica"); n.add("cantidad_reservada_pedidos"); }
                  else { n.delete("cantidad_fisica"); n.delete("cantidad_reservada_pedidos"); }
                  return n;
                });
              }}
              data-testid="filter-descontar-pedidos"
            />
            Descontar lo comprometido en notas de pedido
          </label>
          <div className="flex-1" />
          <Button onClick={consultar} className="bg-[#205c45]" disabled={loading} data-testid="btn-consultar">
            {loading ? "Consultando..." : "Consultar"}
          </Button>
        </div>
      </div>

      {totales.valor_total !== undefined && (
        <>
          <ValorizacionInventario bodegaId={filters.bodega_id} laboratorioId={filters.laboratorio_id} />
          <CierresInventario />
        </>
      )}

      {/* Selector de columnas */}
      <div className="rounded-md border border-[#d1dbd6] bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Columnas visibles · {visibleColumns.length} de {columns.length}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set(columns.map(c => c.key)))}>Todas</Button>
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set(DEFAULT_COLS))}>Predeterminadas</Button>
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}><X size={14} /></Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {columns.map(c => (
            <label key={c.key} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`col-${c.key}`}>
              <Checkbox checked={selected.has(c.key)} onCheckedChange={() => toggleCol(c.key)} />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Totales + Export */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#e9f2ee] border border-[#b2d0c2] rounded-md p-3 text-sm">
        <div className="flex gap-6 tabular-nums">
          <span><strong className="font-semibold">{totales.n_lotes}</strong> <span className="text-[#4a5c53] text-xs uppercase tracking-wider">lotes</span></span>
          <span><strong className="font-semibold">{totales.stock_total?.toLocaleString("es-EC")}</strong> <span className="text-[#4a5c53] text-xs uppercase tracking-wider">{totales.descontar_pedidos ? "uds libres" : "uds stock"}</span></span>
          {totales.descontar_pedidos ? (
            <span data-testid="total-reservado-pedidos"><strong className="font-semibold text-[#8a5a00]">{totales.reservado_total?.toLocaleString("es-EC")}</strong> <span className="text-[#4a5c53] text-xs uppercase tracking-wider">uds en pedidos</span></span>
          ) : null}
          {totales.cajas_total ? (
            <span><strong className="font-semibold">{totales.cajas_total?.toLocaleString("es-EC")}</strong> <span className="text-[#4a5c53] text-xs uppercase tracking-wider">cajas completas</span></span>
          ) : null}
          <span><strong className="font-semibold text-[#205c45]">${totales.valor_total?.toFixed(2)}</strong> <span className="text-[#4a5c53] text-xs uppercase tracking-wider">valor inventario</span></span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => imprimirHojaConteo({
              rows: exportRows,
              columns: exportColumns,
              totales,
              filters: {
                ...filters,
                _bodegaLabel: bodegas.find(b => b.id === filters.bodega_id)?.nombre || "",
                _labLabel: labs.find(l => l.id === filters.laboratorio_id)?.nombre || "",
              },
            })}
            disabled={exportRows.length === 0}
            data-testid="btn-imprimir-conteo"
          >
            <Printer size={14} className="mr-2" />Imprimir hoja de conteo
          </Button>
          <ExportButton
            title="Reporte de inventario"
            columns={exportColumns}
            rows={exportRows}
            filename="reporte-inventario"
            data-testid="inventory-export"
          />
        </div>
      </div>

      {/* Tabla */}
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-auto">
        <table className="erp-table">
          <thead>
            <tr>
              {visibleColumns.map(c => <th key={c.key}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {visibleColumns.map(c => {
                  let v = r[c.key];
                  const isNum = numericKeys.has(c.key);
                  if (c.key === "dias_para_vencer" && typeof v === "number") {
                    const cls = v < 0 ? "fefo-critical" : v <= 90 ? "fefo-warning" : "fefo-safe";
                    return <td key={c.key}><Badge className={`${cls} border`}>{v} días</Badge></td>;
                  }
                  if (c.key === "iva_exento") v = v ? "Sí" : "No";
                  if (["valor_inventario", "valor_inventario_caja"].includes(c.key) && typeof v === "number") v = `$${v.toFixed(2)}`;
                  if (["costo_unitario", "costo_caja"].includes(c.key) && typeof v === "number") v = `$${v.toFixed(4)}`;
                  if (c.key === "precio_venta" && typeof v === "number") v = `$${v.toFixed(2)}`;
                  return <td key={c.key} className={isNum ? "text-right tabular-nums" : ""}>{v == null || v === "" ? "—" : v}</td>;
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={visibleColumns.length || 1} className="text-center py-10 text-[#8ba397]">
                <FileSpreadsheet className="inline mb-2" size={28} /><br />Sin resultados con los filtros actuales
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
